import { useEffect, useState } from "react";
import { apiRequest } from "../../api/httpClient";

export type CodedOption = {
  readonly value: string;
  readonly label: string;
};

const catalogCache = new Map<string, readonly CodedOption[]>();
const pendingRequests = new Map<string, Promise<readonly CodedOption[]>>();

export function clearCatalogCache(): void {
  catalogCache.clear();
  pendingRequests.clear();
}

export async function fetchCatalog(type: string): Promise<readonly CodedOption[]> {
  const cached = catalogCache.get(type);
  if (cached) return cached;

  const pending = pendingRequests.get(type);
  if (pending) return pending;

  const promise = apiRequest<Record<string, Array<{ code: string; displayName: string }>>>(
    `/catalogs/enums?types=${encodeURIComponent(type)}`
  )
    .then((data) => {
      const list = data[type] ?? [];
      const options = list.map((item) => ({ value: item.code, label: item.displayName }));
      catalogCache.set(type, options);
      pendingRequests.delete(type);
      return options;
    })
    .catch((err) => {
      pendingRequests.delete(type);
      throw err;
    });

  pendingRequests.set(type, promise);
  return promise;
}

export async function fetchCatalogs(
  types: readonly string[]
): Promise<Record<string, readonly CodedOption[]>> {
  const result: Record<string, readonly CodedOption[]> = {};
  const missingTypes: string[] = [];

  for (const t of types) {
    const cached = catalogCache.get(t);
    if (cached) {
      result[t] = cached;
    } else {
      missingTypes.push(t);
    }
  }

  if (missingTypes.length === 0) {
    return result;
  }

  const queryTypes = missingTypes.map(encodeURIComponent).join(",");
  const data = await apiRequest<Record<string, Array<{ code: string; displayName: string }>>>(
    `/catalogs/enums?types=${queryTypes}`
  );

  for (const t of missingTypes) {
    const list = data[t] ?? [];
    const options = list.map((item) => ({ value: item.code, label: item.displayName }));
    catalogCache.set(t, options);
    result[t] = options;
  }

  return result;
}

/**
 * Hook nạp danh mục nhãn hiển thị tập trung từ API endpoint GET /catalogs/enums?types=...
 * Có sẵn bộ đệm in-memory chia sẻ toàn ứng dụng để tránh request trùng lặp.
 *
 * @param type tên danh mục (ví dụ 'GRAVE_TYPE', 'CATEGORY_CODE')
 * @param fallbackOptions danh sách tùy chọn dự phòng khi đang tải hoặc lỗi
 */
export function useCatalog(
  type: string,
  fallbackOptions: readonly CodedOption[] = []
): { options: readonly CodedOption[]; loading: boolean; error: Error | null } {
  const [options, setOptions] = useState<readonly CodedOption[]>(() => catalogCache.get(type) ?? fallbackOptions);
  const [loading, setLoading] = useState<boolean>(!catalogCache.has(type));
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (catalogCache.has(type)) {
      setOptions(catalogCache.get(type)!);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    fetchCatalog(type)
      .then((opts) => {
        if (active) {
          setOptions(opts);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (active) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [type]);

  return { options, loading, error };
}
